import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { useContext, useEffect, useState } from 'react';

import { MarsinetContext } from 'apps/marsinet/src/app/context/marsinetProvider';
import { apiServices } from 'apps/marsinet/src/app/services/apiServices';
import { analytics } from 'apps/marsinet/src/firebase';
import { logEvent } from 'firebase/analytics';
import { DeviceTable } from 'apps/marsinet/src/app/typescript-axios';

export interface ModalAssignDeviceToOrganizationProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  assignDeviceToOrganizationFunction: (device: DeviceTable) => void;
  o_id: string;
}

const ModalAssignDeviceToOrganization: React.FC<
  ModalAssignDeviceToOrganizationProps
> = ({ visible, setVisible, assignDeviceToOrganizationFunction, o_id }) => {
  const { t } = useTranslation();
  const { access_token, setError } = useContext(MarsinetContext);
  const [devices, setDevices] = useState<DeviceTable[]>([]);
  const [selectedDevices, setSelectedDevices] = useState<string | null>(null);

  const handleChangeSelectedDevice = (e: { value: string }) => {
    setSelectedDevices(e.value);
  };

  const onpagehide = () => {
    setSelectedDevices(null);
    setVisible(false);
  };

  useEffect(() => {
    const fetchDevices = async () => {
      if (!visible) return;

      try {
        const associatedResponse =
          await apiServices.devicesApi.devicesControllerFindAllDevicesOfOrganization(
            o_id
          );
        const associatedDeviceIds = new Set(
          associatedResponse.data.map((d) => d.d_id)
        );

        const allDevicesResponse =
          await apiServices.devicesApi.devicesControllerFindAll();
        const allDevices = allDevicesResponse.data;

        const unlinkedDevices = allDevices.filter(
          (device: DeviceTable) => !associatedDeviceIds.has(device.d_id)
        );

        setDevices(unlinkedDevices);
      } catch (error) {
        if (error.response?.status !== 401) {
          setError(true);
          logEvent(analytics, 'error_marsinet', {
            error_message: error.message,
            error_code: error.response?.status,
          });
        }
      }
    };

    if (access_token) {
      fetchDevices();
    }
  }, [visible, access_token, o_id]);

  const saveDevices = () => {
    if (!selectedDevices) return;
    const device = devices.find((d) => d.serial === selectedDevices);
    if (!device) return;
    assignDeviceToOrganizationFunction(device);
    setVisible(false);
    setSelectedDevices(null);
  };

  return (
    <Dialog
      header={t('selectDevice')}
      visible={visible}
      className="mx-2 md:w-12 lg:w-8"
      onHide={onpagehide}
      breakpoints={{ '960px': '75vw', '641px': '100vw' }}
    >
      <div className="grid formgrid p-fluid">
        <div className="field mb-5 col-12 py-2">
          <Dropdown
            value={selectedDevices}
            onChange={handleChangeSelectedDevice}
            options={devices}
            optionLabel="serial"
            placeholder={t('pages.devices.num_serial')}
            optionValue="serial"
            showClear
            filter
          />
        </div>
      </div>
      <div className="flex align-items-center justify-content-end">
        <div className="">
          <Button
            id="button-create-device"
            severity="secondary"
            icon="pi pi-save"
            label={t('save')}
            className="p-button-raised"
            onClick={saveDevices}
            disabled={!selectedDevices}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default ModalAssignDeviceToOrganization;
